/**
 * cells/utils.ts
 *
 * Shared helpers for EPC cell components.
 * Pure functions — no React, no side effects.
 */
import {
  EPCData,
  EPCLabel,
  getEPCScore,
} from "shared/components/maintenance/hooks/useEnergyPerformanceData";

export const MAX_VISIBLE_PARTS = 5;

/** Returns whether an EPC entry is missing label/kWh data. */
export const isSpecificEPCIncomplete = (epcEntry: any): boolean => {
  if (!epcEntry?.fileLogId) return false;
  return (
    (!epcEntry.label ||
      epcEntry.label === EPCLabel.UNKNOWN ||
      epcEntry.label === EPCLabel.NIET_VAN_TOEPASSING ||
      epcEntry.label === EPCLabel.ONTBREKEND) &&
    !epcEntry.kWh
  );
};

/** Derives the improvement trend by comparing oldest vs newest EPC in history. */
export const getImprovementTrend = (
  history: EPCData["epcHistory"],
): "improved" | "declined" | "unchanged" | null => {
  if (!history || history.length < 2) return null;
  const sorted = [...history].sort((a, b) => {
    if (!a.documentDate && !b.documentDate) return 0;
    if (!a.documentDate) return 1;
    if (!b.documentDate) return -1;
    return (
      new Date(a.documentDate).getTime() - new Date(b.documentDate).getTime()
    );
  });
  const oldest = sorted[0];
  const newest = sorted[sorted.length - 1];
  const oldScore = getEPCScore(oldest.label);
  const newScore = getEPCScore(newest.label);
  if (newScore > oldScore) return "improved";
  if (newScore < oldScore) return "declined";
  return "unchanged";
};

/** Formats a date string to Dutch locale (dd-mm-yyyy). */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "Onbekende datum";
  try {
    return new Date(dateStr).toLocaleDateString("nl-NL");
  } catch {
    return dateStr;
  }
}

/** Formats a date string as dd-mm-yyyy for Excel export. */
export function formatDateForExport(
  dateStr: string | null | undefined,
): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
}

/** Picks the effective date from a project part (effectiveStartDate > actualStartDate > plannedStartDate). */
export function getPartEffectiveDate(part: {
  effectiveStartDate: string | null;
  actualStartDate: string | null;
  plannedStartDate: string | null;
}): string | null {
  return part.effectiveStartDate ?? part.actualStartDate ?? part.plannedStartDate;
}
